import { Link } from 'react-router';
import { countSession, type OpsContainer, type OpsTrip } from '@ecobox/firebase';
import { Badge, Button, TimeInState, useNow, type BadgeTone } from '@ecobox/ui';
import { SessionCards } from '../components/SessionCards';
import { DriverToday } from './driver/DriverToday';
import {
  useExportContainers,
  useImportContainers,
  useInboundTrips,
  useLoadingTrips,
} from '../hooks/useOps';
import {
  containerLabel,
  containerStatusLabel,
  containerStatusTone,
  dueClock,
  tripLabel,
  tripStatusLabel,
} from '../labels';
import { useBranch, useSession, useSyncState } from '../services';
import { useAllSessions, useLedgerById, usePermissions } from '../sessions';

interface Thing {
  key: string;
  title: string;
  status: string;
  tone: BadgeTone;
  since: string;
  nextDueAt: string | undefined;
  to: string;
}

const SHOWN = 8;

function tripThing(t: OpsTrip, to: string): Thing {
  return {
    key: t.id,
    title: tripLabel(t),
    status: tripStatusLabel(t.status),
    tone: t.status === 'loading' ? 'info' : 'warning',
    since: t.stateSince,
    nextDueAt: t.nextDueAt,
    to,
  };
}

function containerThing(c: OpsContainer, to: string): Thing {
  return {
    key: c.id,
    title: containerLabel(c),
    status: containerStatusLabel(c.status),
    tone: containerStatusTone(c.status),
    since: c.stateSince,
    nextDueAt: c.nextDueAt,
    to,
  };
}

export function TodayPage() {
  const session = useSession();
  const branch = useBranch().current;
  const sync = useSyncState();
  const perms = usePermissions();
  const sessions = useAllSessions();
  const ledger = useLedgerById();
  const name = session.profile?.displayName ?? session.profile?.username ?? 'there';
  const canLoad = perms.has('trip.load') || perms.has('container.load');
  const canReceive = perms.has('trip.unload');

  const loadingTrips = useLoadingTrips(canLoad ? branch : null);
  const inboundTrips = useInboundTrips(canReceive ? branch : null);
  const exportContainers = useExportContainers(canLoad ? branch : null);
  const importContainers = useImportContainers(canReceive ? branch : null);

  const mine = sessions.filter((s) => s.uid === session.uid && s.finishedAt === undefined);
  const unsentScans = mine.reduce((n, s) => n + countSession(s, ledger).unsent, 0);

  const things: Thing[] = [
    ...loadingTrips.data.map((t) => tripThing(t, '/load')),
    ...inboundTrips.data.map((t) => tripThing(t, '/receive')),
    ...exportContainers.data.map((c) => containerThing(c, '/load')),
    ...importContainers.data.map((c) => containerThing(c, '/receive')),
  ].sort((a, b) => a.since.localeCompare(b.since));
  const liveError =
    loadingTrips.error ?? inboundTrips.error ?? exportContainers.error ?? importContainers.error;
  const now = useNow();

  return (
    <div className="ops-stack">
      <div>
        <h1 className="ops-title">Hello, {name}</h1>
        <p className="ops-muted" style={{ margin: 0 }}>
          Working at {branch ?? '—'}
          {sync.online ? '' : ' · offline'}
          {' · '}
          <TimeInState
            since={new Date(sync.since).toISOString()}
            suffix={sync.online ? 'online' : 'offline'}
          />
        </p>
      </div>

      <DriverToday />

      <div className="ops-tiles" data-tour="sync-tiles">
        <div className="ops-tile" data-tone={sync.pending > 0 ? 'wait' : undefined}>
          <div className="ops-tile__n">{sync.pending}</div>
          <div className="ops-muted">waiting to sync</div>
        </div>
        <div className="ops-tile" data-tone={sync.rejected > 0 ? 'bad' : undefined}>
          <div className="ops-tile__n">{sync.rejected}</div>
          <div className="ops-muted">rejected</div>
        </div>
        {unsentScans > 0 ? (
          <div className="ops-tile" data-tone="wait">
            <div className="ops-tile__n">{unsentScans}</div>
            <div className="ops-muted">scans not sent yet</div>
          </div>
        ) : null}
        {sync.notDelivered > 0 ? (
          <div className="ops-tile" data-tone="bad">
            <div className="ops-tile__n">{sync.notDelivered}</div>
            <div className="ops-muted">not delivered</div>
          </div>
        ) : null}
      </div>

      <div className="ops-two" data-tour="today-actions">
        {canLoad ? (
          <Link to="/load">
            <Button size="touch" variant="primary" block>
              Load
            </Button>
          </Link>
        ) : null}
        {canReceive ? (
          <Link to="/receive">
            <Button size="touch" variant="primary" block>
              Receive
            </Button>
          </Link>
        ) : null}
        {perms.has('container.manage') || canReceive ? (
          <Link to="/yard">
            <Button size="touch" variant="secondary" block>
              Container yard
            </Button>
          </Link>
        ) : null}
        {perms.has('exception.raise') ? (
          <Link to="/exception">
            <Button size="touch" variant="secondary" block>
              Report a problem
            </Button>
          </Link>
        ) : null}
      </div>

      <section className="ops-stack" aria-label="My sessions in progress" data-tour="my-sessions">
        <h2 className="ops-subtitle">My sessions in progress</h2>
        {mine.length === 0 ? (
          <p className="ops-muted" style={{ margin: 0 }}>
            None. Start one from Load or Receive.
          </p>
        ) : (
          <SessionCards sessions={mine} />
        )}
      </section>

      {canLoad || canReceive ? (
        <section
          className="ops-stack"
          aria-label={`At ${branch ?? 'my branch'}`}
          data-tour="branch-board"
        >
          <h2 className="ops-subtitle">At {branch ?? 'my branch'}, longest in state first</h2>
          {liveError ? (
            <p className="ops-muted" style={{ margin: 0 }}>
              Some live data is not available right now.
            </p>
          ) : null}
          {things.length === 0 ? (
            <p className="ops-muted" style={{ margin: 0 }}>
              Nothing is waiting here.
            </p>
          ) : (
            <ul className="ops-list" aria-label="Trips and containers">
              {things.slice(0, SHOWN).map((t) => {
                const clock = dueClock(t.nextDueAt, now);
                return (
                  <li
                    key={t.key}
                    className="ops-row"
                    data-tone={
                      clock === 'breached' ? 'bad' : clock === 'at_risk' ? 'waiting' : undefined
                    }
                  >
                    <div className="ops-row__top">
                      <strong>{t.title}</strong>
                      <Badge tone={t.tone}>{t.status}</Badge>
                    </div>
                    <div className="ops-muted">
                      <TimeInState since={t.since} clock={clock} suffix={t.status.toLowerCase()} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {things.length > SHOWN ? (
            <p className="ops-muted" style={{ margin: 0 }}>
              {things.length - SHOWN} more. See Load and Receive.
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
